import React from 'react';
import { Phone, Building2, Flag, Landmark } from 'lucide-react';
import { Avatar, PartyChip } from './PersonAtoms';
import DistrictMapLink from '../DistrictMapLink';
import CommitteeLink from '../committee/CommitteeLink';
import { EmailIcon, OpenExternalIcon } from '../icons';
import { formatPersonName } from '../../utils/nameUtils';
import { formatDate, parseLocalDate } from '../../utils/dateUtils';
import { formatDistrict } from '../../utils/districtDisplay';
import { formatPartyDisplay } from '../../utils/partyUtils';
import { ExternalTextLink, MUTED_TEXT } from '../ballot-initiatives/detail/detailAtoms';
import {
  generalElectionDate,
  legislatorOfficeTitle,
  personKeyFacts,
} from './personDisplay';
import type { PersonData, SocialMediaAccount } from './types';

interface PersonHeroProps {
  person: PersonData;
  /** Pro actions (notes, custom fields, track) shown above the contact rows. */
  actions?: React.ReactNode;
}

function stripUrlForDisplay(url: string): string {
  return url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '');
}

const withScheme = (url: string): string => (/^https?:\/\//i.test(url) ? url : `https://${url}`);

// Platform icon SVGs (lucide-react doesn't have brand icons)
const PLATFORM_ICONS: Record<string, React.FC<{ className?: string }>> = {
  twitter: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
  ),
  x: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
  ),
  instagram: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" /></svg>
  ),
  facebook: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>
  ),
  linkedin: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
  ),
  youtube: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" /></svg>
  ),
};

function getSocialIcon(platform: string): React.FC<{ className?: string }> | null {
  const key = platform.toLowerCase().replace(/\s+/g, '');
  return PLATFORM_ICONS[key] || null;
}

const CHIP = 'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium';

interface ContactRow {
  key: string;
  label: string;
  subLabel: string;
  href: string;
  external: boolean;
  Icon: React.ElementType;
}

/**
 * The person's contact rows, in the order a reader reaches for them. Sub-labels
 * say whose line it is: a legislator's phone is the Capitol office; a
 * candidate-only person's website is the campaign's.
 */
export function personContactRows(person: PersonData): ContactRow[] {
  const contacts = person.legislator?.contacts || person.contacts || null;
  if (!contacts) return [];
  const isLeg = person.roles.includes('legislator');
  const isCandOnly = person.roles.includes('candidate') && !isLeg && !person.roles.includes('officeholder');
  const rows: ContactRow[] = [];
  if (contacts.phone) {
    rows.push({
      key: 'phone',
      label: contacts.phone,
      subLabel: isLeg ? 'Capitol phone' : person.roles.includes('officeholder') ? 'Office phone' : 'Phone',
      href: `tel:${contacts.phone}`,
      external: false,
      Icon: Phone,
    });
  }
  if (contacts.website) {
    rows.push({
      key: 'website',
      label: stripUrlForDisplay(contacts.website),
      subLabel: isCandOnly ? 'Campaign website' : 'Website',
      href: withScheme(contacts.website),
      external: true,
      Icon: OpenExternalIcon,
    });
  }
  if (contacts.email) {
    rows.push({
      key: 'email',
      label: contacts.email,
      subLabel: isCandOnly ? 'Campaign email' : 'Email',
      href: `mailto:${contacts.email}`,
      external: false,
      Icon: EmailIcon,
    });
  }
  return rows;
}

/**
 * The one-sentence summary under the name, built from the person's roles —
 * "State senator for CO SD-04. Republican candidate for U.S. Senate,
 * challenging incumbent John Hickenlooper, Nov 3, 2026." The district stays a
 * district-map link.
 */
const PersonSummary: React.FC<{ person: PersonData }> = ({ person }) => {
  const legislator = person.roles.includes('legislator') ? person.legislator : null;
  const candidate = person.roles.includes('candidate') ? person.candidate : null;
  const officeholder = person.roles.includes('officeholder') ? person.officeholder : null;
  const sentences: React.ReactNode[] = [];

  if (legislator) {
    sentences.push(
      <React.Fragment key="leg">
        {legislatorOfficeTitle(legislator.chamber)} for{' '}
        <DistrictMapLink chamber={legislator.chamber} district={legislator.district} showIcon>
          {formatDistrict(null, String(legislator.district ?? ''), legislator.chamber)}
        </DistrictMapLink>
        .
      </React.Fragment>
    );
  } else if (officeholder) {
    const where = [officeholder.organization, officeholder.jurisdiction].filter(Boolean).join(', ');
    sentences.push(<React.Fragment key="off">{officeholder.title}{where ? `, ${where}` : ''}.</React.Fragment>);
  }

  if (candidate) {
    const party = formatPartyDisplay(candidate.party_affiliation ?? '') || candidate.party_affiliation;
    const office = (candidate.office_sought ?? '').split(' · ')[0];
    const district = candidate.district_display ?? candidate.district;
    const general = generalElectionDate(candidate);
    const upcoming = general && parseLocalDate(general).getTime() >= new Date().setHours(0, 0, 0, 0);
    sentences.push(
      <React.Fragment key="cand">
        {party ? `${party} candidate` : 'Candidate'} for {office}
        {district && !/\d/.test(office) && (
          <>
            {' in '}
            <DistrictMapLink chamber={candidate.office_sought} district={candidate.district} showIcon>
              {district}
            </DistrictMapLink>
          </>
        )}
        {candidate.is_incumbent
          ? ', seeking re-election'
          : candidate.seat_incumbent_name ? `, challenging incumbent ${candidate.seat_incumbent_name}` : ''}
        {upcoming ? ` in the general election on ${formatDate(general, { month: 'long' })}` : ''}
        .
      </React.Fragment>
    );
  }

  if (sentences.length === 0) return null;
  return (
    <p className="max-w-[720px] text-[17px] leading-normal text-text-secondary dark:text-text-dark-secondary">
      {sentences.map((sentence, index) => (
        <React.Fragment key={index}>{index > 0 ? ' ' : ''}{sentence}</React.Fragment>
      ))}
    </p>
  );
};

/**
 * Person hero, in the measure detail's language: avatar, role and party chips,
 * a Poppins name and a one-sentence summary; actions and contact rows on the
 * right; then Key facts and, for a sitting legislator, committees. The
 * countdown panel and mini timeline are gone — the path to the ballot is its
 * own section at the end of the page.
 */
const PersonHero: React.FC<PersonHeroProps> = ({ person, actions }) => {
  const isLeg = person.roles.includes('legislator');
  const isCand = person.roles.includes('candidate');
  const isOfficeholder = person.roles.includes('officeholder');
  const party = person.legislator?.party_affiliation || person.candidate?.party_affiliation || person.officeholder?.party;
  const committees = isLeg ? person.legislator?.committees ?? [] : [];
  const socialMedia: SocialMediaAccount[] =
    person.legislator?.contacts?.social_media || person.social_media || [];
  const contacts = personContactRows(person);
  const facts = personKeyFacts(person);

  return (
    <section className="card p-6 md:p-7" aria-labelledby="person-name">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-[112px_minmax(0,1fr)] lg:grid-cols-[112px_minmax(0,1fr)_260px] lg:gap-7">
        <Avatar name={person.full_name} photoUrl={person.photo_url} party={party} size={112} />

        <div className="flex min-w-0 flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            {isOfficeholder && (
              <span className={`${CHIP} border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-700 dark:bg-slate-900/20 dark:text-slate-300`}>
                <Landmark className="h-3.5 w-3.5" aria-hidden="true" />
                {person.officeholder?.title || 'Officeholder'}
              </span>
            )}
            {isLeg && (
              <span className={`${CHIP} border-primary-200 bg-primary-50 text-primary-800 dark:border-primary-dark-200 dark:bg-primary-dark-50 dark:text-primary-dark-500`}>
                <Building2 className="h-3.5 w-3.5" aria-hidden="true" />
                Sitting {person.legislator?.chamber}
              </span>
            )}
            {isCand && (
              <span className={`${CHIP} border-warning-200 bg-warning-100 text-warning-800 dark:border-warning-700 dark:bg-warning-900/30 dark:text-warning-300`}>
                <Flag className="h-3.5 w-3.5" aria-hidden="true" />
                {person.candidate?.election_year} Candidate
              </span>
            )}
            {party && <PartyChip party={party} />}
          </div>

          <h1
            id="person-name"
            className="font-display text-[28px] font-semibold leading-[1.1] tracking-[-0.02em] text-text-primary dark:text-text-dark-primary md:text-[34px]"
          >
            {formatPersonName(person)}
          </h1>

          <PersonSummary person={person} />
        </div>

        {(actions || contacts.length > 0 || socialMedia.length > 0) && (
          <div className="flex flex-col gap-2.5 md:col-span-2 lg:col-span-1">
            {actions && <div className="flex flex-wrap items-center gap-2 lg:justify-end">{actions}</div>}
            {contacts.map((row) => (
              <a
                key={row.key}
                href={row.href}
                {...(row.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                className="flex items-center justify-between gap-3 rounded-[10px] border border-border-light bg-background-secondary px-3 py-2.5 transition-colors hover:border-primary-300 dark:border-border-dark dark:bg-background-dark-tertiary dark:hover:border-primary-400"
              >
                <span className="min-w-0">
                  <span className="block truncate text-sm font-semibold text-text-primary dark:text-text-dark-primary">{row.label}</span>
                  <span className={`block text-xs ${MUTED_TEXT}`}>{row.subLabel}</span>
                </span>
                <row.Icon className="h-3.5 w-3.5 flex-shrink-0 text-primary-400" aria-hidden="true" />
              </a>
            ))}
            {socialMedia.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 pt-0.5">
                {socialMedia.map((sm, i) => {
                  const Icon = getSocialIcon(sm.platform);
                  if (!Icon || !sm.url) return null;
                  const handleLabel = sm.handle ? `@${sm.handle.replace(/^@/, '')}` : sm.platform;
                  return (
                    <a
                      key={i}
                      href={sm.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={handleLabel}
                      aria-label={`${sm.platform}: ${handleLabel}`}
                      className="flex h-7 w-7 items-center justify-center rounded-full border border-border-light text-text-secondary transition-colors hover:border-text-tertiary hover:text-text-primary dark:border-border-dark dark:text-text-dark-secondary dark:hover:border-text-dark-tertiary dark:hover:text-text-dark-primary"
                    >
                      <Icon className="h-3.5 w-3.5" />
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {facts.length > 0 && (
        <dl className="mt-6 grid grid-cols-1 gap-x-5 gap-y-4 border-t border-border-light pt-5 dark:border-border-dark sm:grid-cols-2 lg:grid-cols-5">
          {facts.map(({ label, value, committee, href }) => (
            <div key={label} className="min-w-0">
              <dt className={`text-[11px] font-bold uppercase tracking-[0.12em] ${MUTED_TEXT}`}>{label}</dt>
              <dd className="mt-1 text-sm font-medium leading-[1.45] text-text-primary dark:text-text-dark-primary">
                {committee
                  ? <CommitteeLink coId={committee.coId} name={value} className="text-primary-700 dark:text-primary-dark-400" />
                  : href
                    ? <ExternalTextLink href={href}>{value}</ExternalTextLink>
                    : value}
              </dd>
            </div>
          ))}
        </dl>
      )}

      {committees.length > 0 && (
        <div className="mt-5 flex flex-col gap-2 border-t border-border-light pt-5 dark:border-border-dark md:flex-row md:gap-4">
          <div className={`text-[11px] font-bold uppercase tracking-[0.12em] md:w-[180px] md:flex-shrink-0 md:pt-1.5 ${MUTED_TEXT}`}>
            Committees
          </div>
          {/* Legislative committees have no page of their own yet, so the pills are labels, not links. */}
          <ul className="flex flex-wrap gap-2">
            {committees.map((c, i) => (
              <li
                key={i}
                className="inline-flex items-center rounded-full border border-border-light bg-background-secondary px-3 py-1.5 text-[13px] font-medium text-text-primary dark:border-border-dark dark:bg-background-dark-tertiary dark:text-text-dark-primary"
              >
                {c.committee_name}
                {c.role && c.role !== 'Member' && (
                  <span className={`ml-1.5 text-[10px] font-bold uppercase tracking-wide ${MUTED_TEXT}`}>· {c.role}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};

export default PersonHero;
